import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { MapPin, Clock, IndianRupee, Users, Table2, Filter, X, Check } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PhonePromptBanner from "../components/PhonePromptBanner";
import ComingSoonBanner from "../components/ComingSoonBanner";
import SEO from "../components/SEO";
import { parseEventTime } from "../utils/parseEventTime";
import { websiteJsonLd } from "../utils/eventJsonLd";
import { formatEventDate } from "../utils/formatEventDate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAHJONG_STYLES = ["Taiwanese", "Willingdon", "ATF Western", "Singapore", "International", "Japanese", "Hong Kong", "American", "Chinese"];

// Format date as "Wed, June 17, 2026" — empty/invalid dates stay blank.
const formatDate = (dateStr) => formatEventDate(dateStr);

// Format payment timing array to readable string. Defensively coerces
// non-array shapes (legacy / malformed rows have been seen with a single
// string instead of a list — see /dev/create-test-event prior to fix).
const formatPaymentTiming = (timing) => {
  if (!timing) return null;
  const arr = Array.isArray(timing) ? timing : [timing];
  if (arr.length === 0) return null;
  const labels = { at_venue: "at venue", in_advance: "in advance" };
  return arr.map(t => labels[t] || t).join(" or ");
};

// Format time to 12h display (handles both "16:00" and "2:00 PM")
const formatTime = (timeStr) => {
  if (!timeStr) return "";
  const upper = timeStr.toUpperCase().trim();
  if (upper.includes("AM") || upper.includes("PM")) return timeStr.trim();
  const [hours, minutes] = timeStr.split(":");
  const h = parseInt(hours, 10);
  const suffix = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${suffix}`;
};

const fetchWithRetry = async (fn, retries = 2, delay = 1000) => {
  for (let i = 0; i < retries; i++) {
    try {
      return await fn();
    } catch (err) {
      if (i === retries - 1) throw err;
      await new Promise(res => setTimeout(res, delay));
    }
  }
};

const HomePage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null); // null = no error, string = error message
  const [currentUser, setCurrentUser] = useState(null);
  const [userSignups, setUserSignups] = useState({}); // { eventId: true/false }
  
  // Filter state
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [classesFilter, setClassesFilter] = useState(""); // "", "yes", "no"
  const [styleFilter, setStyleFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [devTestEvent, setDevTestEvent] = useState(null);
  const [devLoading, setDevLoading] = useState(false);

  useEffect(() => {
    // Scroll to top when homepage loads
    window.scrollTo(0, 0);
    fetchEvents();
    fetchCities();
    checkAuth();
  }, []);

  const fetchCities = async () => {
    try {
      const res = await api.get(`${API}/events/cities`);
      setCities(res.data);
    } catch (error) {
      // error intentionally suppressed
    }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem("user_token");
    if (token) {
      try {
        const res = await api.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        setCurrentUser(res.data);
        // Fetch user's signups
        fetchUserSignups(res.data.id);
      } catch (e) {
        setCurrentUser(null);
      }
    }
  };

  const fetchUserSignups = async (userId) => {
    try {
      const token = localStorage.getItem('user_token');
      const res = await api.get(`${API}/users/${userId}/signups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Convert array to object for quick lookup
      const signups = {};
      res.data.forEach(eventId => {
        signups[eventId] = true;
      });
      setUserSignups(signups);
    } catch (e) {
      // error intentionally suppressed
    }
  };

  const fetchEvents = async () => {
    setFetchError(false);
    try {
      // Attach bearer token when available so admins receive private events
      // from /events/stats. Non-admins get the same filtered payload they
      // always did (the backend uses get_optional_user — an invalid token
      // resolves to None, which is treated as a public caller).
      const token = localStorage.getItem("user_token");
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
      const response = await fetchWithRetry(() => api.get(`${API}/events/stats`, config));
      // Filter out cancelled events from public view
      const data = Array.isArray(response.data) ? response.data : [];
      const activeEvents = data.filter(event => event.status !== "cancelled");
      setEvents(activeEvents);
    } catch (error) {
      console.error("fetchEvents failed:", error.message, error.stack);
      const errMsg = error.code === "ECONNABORTED" ? "Timeout"
        : error.response ? `HTTP ${error.response.status}`
        : error.request ? "No response (network)"
        : error.message || "Unknown";
      setFetchError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: "badge-open",
      full: "badge-full",
      locked: "badge-locked",
    };
    return badges[status] || "badge-open";
  };

  const createTestEvent = async () => {
    const token = localStorage.getItem("user_token");
    setDevLoading(true);
    try {
      const res = await fetch(`${API}/dev/create-test-event`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setDevTestEvent(data);
      } else {
        alert("Failed: " + (data.detail || "Unknown error"));
      }
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setDevLoading(false);
    }
  };

  if (loading) {
    const SkeletonCard = () => (
      <div style={{
        height: "100%", position: "relative", overflow: "hidden",
        padding: 0, border: "2px solid #e0e0e0", borderRadius: "12px", background: "#fff"
      }}>
        <div style={{
          height: "140px",
          background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
          backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite"
        }} />
        <div style={{ padding: "0.85rem" }}>
          <div style={{ height: "16px", background: "#f0f0f0", borderRadius: "4px", marginBottom: "0.75rem", width: "80%" }} />
          <div style={{ height: "12px", background: "#f0f0f0", borderRadius: "4px", marginBottom: "0.5rem", width: "60%" }} />
          <div style={{ height: "12px", background: "#f0f0f0", borderRadius: "4px", marginBottom: "0.5rem", width: "50%" }} />
          <div style={{ height: "12px", background: "#f0f0f0", borderRadius: "4px", marginBottom: "1rem", width: "40%" }} />
          <div style={{ height: "36px", background: "#f0f0f0", borderRadius: "8px", width: "100%" }} />
        </div>
      </div>
    );

    return (
      <div>
        <style>{`
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
        <SEO
          title="Find Local Mahjong Games"
          description="Find and join local Mahjong games in your area. Sign up for events, get matched with players at your skill level."
          path="/"
          jsonLd={websiteJsonLd()}
        />
        <Header />
        <PhonePromptBanner />
        <ComingSoonBanner />
        <div className="container" style={{ paddingTop: "0.75rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.5rem" }}>
            <div style={{ height: "28px", width: "200px", background: "#f0f0f0", borderRadius: "4px" }} />
            <div style={{ height: "32px", width: "80px", background: "#f0f0f0", borderRadius: "6px" }} />
          </div>
          <div style={{
            display: "grid", gap: "1.5rem",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))"
          }}>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </div>
      </div>
    );
  }

  if (fetchError) {
    const handleRetry = () => {
      setFetchError(null);
      setLoading(true);
      fetchEvents();
    };
    return (
      <div>
        <SEO
          title="Find Local Mahjong Games"
          description="Find and join local Mahjong games in your area. Sign up for events, get matched with players at your skill level."
          path="/"
          jsonLd={websiteJsonLd()}
        />
        <Header />
        <PhonePromptBanner />
        <ComingSoonBanner />
        <div className="container" style={{ paddingTop: "0.75rem" }}>
          <div className="card" style={{ textAlign: "center", padding: "3rem" }}>
            <h2 style={{ fontSize: "1.5rem", color: "#1B4D3E", marginBottom: "1rem" }}>
              Couldn't load events
            </h2>
            <p style={{ fontSize: "1rem", color: "#666660", marginBottom: "1.5rem" }}>
              There was a problem loading events. Please try again.
            </p>
            <p style={{ fontSize: "0.7rem", color: "#999", marginBottom: "1rem" }}>
              Error: {fetchError}
            </p>
            <button
              onClick={handleRetry}
              className="btn btn-primary"
              data-testid="retry-fetch-events"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SEO
        title="Find Local Mahjong Games"
        description="Find and join local Mahjong games in your area. Sign up for events, get matched with players at your skill level."
        path="/"
        jsonLd={websiteJsonLd()}
      />
      <Header />
      <PhonePromptBanner />
      <ComingSoonBanner />

      <div className="container" style={{ paddingTop: "0.75rem" }}>
        {events.length === 0 ? (
          <div className="card" style={{ textAlign: "center", padding: "3rem" }}>
            <p style={{ fontSize: "1.25rem", color: "#666660" }}>
              No events scheduled yet.
            </p>
          </div>
        ) : (
          <>
            {/* Page Title + Filter Button Row */}
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              marginTop: "1.5rem",
              marginBottom: "1.5rem",
              gap: "0.75rem"
            }}>
              <h1 style={{
                color: "#1B4D3E",
                fontSize: "1.25rem",
                fontWeight: "700",
                margin: 0
              }}>
                Upcoming Events
              </h1>
              
              {/* Filter Button - Compact */}
              <button
                onClick={() => setShowFilters(true)}
                style={{
                  padding: "0.25rem 0.5rem",
                  background: (selectedCity || classesFilter || styleFilter) ? "#1B4D3E" : "transparent",
                  color: (selectedCity || classesFilter || styleFilter) ? "#FFF" : "#666",
                  border: "1px solid #CCC",
                  borderRadius: "4px",
                  fontSize: "0.7rem",
                  fontWeight: "500",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.25rem"
                }}
                data-testid="open-filters-btn"
              >
                <Filter size={12} />
                Filter
                {(selectedCity || classesFilter || styleFilter) && (
                  <span style={{ 
                    background: "#FFF", 
                    color: "#1B4D3E", 
                    borderRadius: "50%", 
                    width: "14px",
                    height: "14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.6rem",
                    fontWeight: "700"
                  }}>
                    {[selectedCity, classesFilter, styleFilter].filter(Boolean).length}
                  </span>
                )}
              </button>
            </div>
            
            {/* Active Filter Pills */}
            {(selectedCity || classesFilter || styleFilter) && (
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1rem" }}>
                {selectedCity && (
                  <span style={{
                    padding: "0.5rem 0.75rem",
                    background: "#E8F5E9",
                    color: "#1B4D3E",
                    borderRadius: "20px",
                    fontSize: "0.85rem",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.35rem"
                  }}>
                    {selectedCity}
                    <button 
                      onClick={() => setSelectedCity("")}
                      style={{ background: "none", border: "none", padding: "2px", cursor: "pointer", display: "flex" }}
                      data-testid="clear-city-filter"
                    >
                      <X size={14} color="#1B4D3E" />
                    </button>
                  </span>
                )}
                {classesFilter && (
                  <span style={{
                    padding: "0.5rem 0.75rem",
                    background: "#E8F5E9",
                    color: "#1B4D3E",
                    borderRadius: "20px",
                    fontSize: "0.85rem",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.35rem"
                  }}>
                    {classesFilter === "yes" ? "Classes" : "No Classes"}
                    <button 
                      onClick={() => setClassesFilter("")}
                      style={{ background: "none", border: "none", padding: "2px", cursor: "pointer", display: "flex" }}
                      data-testid="clear-classes-filter"
                    >
                      <X size={14} color="#1B4D3E" />
                    </button>
                  </span>
                )}
                {styleFilter && (
                  <span style={{
                    padding: "0.5rem 0.75rem",
                    background: "#E8F5E9",
                    color: "#1B4D3E",
                    borderRadius: "20px",
                    fontSize: "0.85rem",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.35rem"
                  }}>
                    {styleFilter}
                    <button 
                      onClick={() => setStyleFilter("")}
                      style={{ background: "none", border: "none", padding: "2px", cursor: "pointer", display: "flex" }}
                      data-testid="clear-style-filter"
                    >
                      <X size={14} color="#1B4D3E" />
                    </button>
                  </span>
                )}
              </div>
            )}
            
            {/* Filter Bottom Sheet / Modal - Mobile Friendly */}
            {showFilters && (
              <>
                {/* Backdrop */}
                <div 
                  onClick={() => setShowFilters(false)}
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "rgba(0,0,0,0.5)",
                    zIndex: 999
                  }}
                  data-testid="filter-backdrop"
                />
                {/* Bottom Sheet */}
                <div style={{
                  position: "fixed",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: "#FFF",
                  borderRadius: "20px 20px 0 0",
                  padding: "1.5rem",
                  paddingBottom: "2rem",
                  zIndex: 1000,
                  maxHeight: "85vh",
                  overflowY: "auto",
                  boxShadow: "0 -4px 20px rgba(0,0,0,0.15)"
                }}
                data-testid="filter-sheet"
                >
                  {/* Handle bar */}
                  <div style={{ 
                    width: "40px", 
                    height: "4px", 
                    background: "#DDD", 
                    borderRadius: "2px", 
                    margin: "0 auto 1rem" 
                  }} />
                  
                  {/* Header */}
                  <div style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center", 
                    marginBottom: "1.5rem" 
                  }}>
                    <h2 style={{ fontSize: "1.25rem", fontWeight: "700", color: "#1B4D3E", margin: 0 }}>
                      Filter Events
                    </h2>
                    <button 
                      onClick={() => setShowFilters(false)}
                      style={{ 
                        background: "#F5F5F5", 
                        border: "none", 
                        borderRadius: "50%", 
                        width: "36px", 
                        height: "36px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer"
                      }}
                      data-testid="close-filters-btn"
                    >
                      <X size={20} color="#666" />
                    </button>
                  </div>
                  
                  {/* City Filter - First */}
                  {cities.length > 0 && (
                    <div style={{ marginBottom: "1.5rem" }}>
                      <label style={{ 
                        display: "block", 
                        fontSize: "0.9rem", 
                        fontWeight: "600", 
                        marginBottom: "0.75rem", 
                        color: "#333" 
                      }}>
                        City
                      </label>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                        <button
                          onClick={() => setSelectedCity("")}
                          style={{
                            padding: "0.75rem 1.25rem",
                            background: selectedCity === "" ? "#1B4D3E" : "#F5F7F5",
                            color: selectedCity === "" ? "#FFF" : "#1B4D3E",
                            border: selectedCity === "" ? "2px solid #1B4D3E" : "2px solid #E0E0E0",
                            borderRadius: "25px",
                            fontSize: "0.95rem",
                            fontWeight: "600",
                            cursor: "pointer",
                            minHeight: "44px",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.35rem"
                          }}
                          data-testid="filter-all-cities"
                        >
                          {selectedCity === "" && <Check size={16} />}
                          All Cities
                        </button>
                        {cities.map(city => (
                          <button
                            key={city}
                            onClick={() => setSelectedCity(city)}
                            style={{
                              padding: "0.75rem 1.25rem",
                              background: selectedCity === city ? "#1B4D3E" : "#F5F7F5",
                              color: selectedCity === city ? "#FFF" : "#1B4D3E",
                              border: selectedCity === city ? "2px solid #1B4D3E" : "2px solid #E0E0E0",
                              borderRadius: "25px",
                              fontSize: "0.95rem",
                              fontWeight: "600",
                              cursor: "pointer",
                              minHeight: "44px",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.35rem"
                            }}
                            data-testid={`filter-city-${city.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {selectedCity === city && <Check size={16} />}
                            {city}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Classes Available Filter */}
                  <div style={{ marginBottom: "1.5rem" }}>
                    <label style={{ 
                      display: "block", 
                      fontSize: "0.9rem", 
                      fontWeight: "600", 
                      marginBottom: "0.75rem", 
                      color: "#333" 
                    }}>
                      Classes Available
                    </label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                      {[
                        { value: "", label: "Any" },
                        { value: "yes", label: "Yes - With Classes" },
                        { value: "no", label: "No Classes" }
                      ].map(option => (
                        <button
                          key={option.value}
                          onClick={() => setClassesFilter(option.value)}
                          style={{
                            padding: "0.75rem 1.25rem",
                            background: classesFilter === option.value ? "#1B4D3E" : "#F5F7F5",
                            color: classesFilter === option.value ? "#FFF" : "#1B4D3E",
                            border: classesFilter === option.value ? "2px solid #1B4D3E" : "2px solid #E0E0E0",
                            borderRadius: "25px",
                            fontSize: "0.95rem",
                            fontWeight: "600",
                            cursor: "pointer",
                            minHeight: "44px",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.35rem"
                          }}
                          data-testid={`filter-classes-${option.value || 'any'}`}
                        >
                          {classesFilter === option.value && <Check size={16} />}
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Style of Play Filter */}
                  <div style={{ marginBottom: "1.5rem" }}>
                    <label style={{ 
                      display: "block", 
                      fontSize: "0.9rem", 
                      fontWeight: "600", 
                      marginBottom: "0.75rem", 
                      color: "#333" 
                    }}>
                      Style of Play
                    </label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                      <button
                        onClick={() => setStyleFilter("")}
                        style={{
                          padding: "0.75rem 1.25rem",
                          background: styleFilter === "" ? "#1B4D3E" : "#F5F7F5",
                          color: styleFilter === "" ? "#FFF" : "#1B4D3E",
                          border: styleFilter === "" ? "2px solid #1B4D3E" : "2px solid #E0E0E0",
                          borderRadius: "25px",
                          fontSize: "0.95rem",
                          fontWeight: "600",
                          cursor: "pointer",
                          minHeight: "44px",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.35rem"
                        }}
                        data-testid="filter-style-any"
                      >
                        {styleFilter === "" && <Check size={16} />}
                        Any Style
                      </button>
                      {MAHJONG_STYLES.map(style => (
                        <button
                          key={style}
                          onClick={() => setStyleFilter(style)}
                          style={{
                            padding: "0.75rem 1.25rem",
                            background: styleFilter === style ? "#1B4D3E" : "#F5F7F5",
                            color: styleFilter === style ? "#FFF" : "#1B4D3E",
                            border: styleFilter === style ? "2px solid #1B4D3E" : "2px solid #E0E0E0",
                            borderRadius: "25px",
                            fontSize: "0.95rem",
                            fontWeight: "600",
                            cursor: "pointer",
                            minHeight: "44px",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.35rem"
                          }}
                          data-testid={`filter-style-${style.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {styleFilter === style && <Check size={16} />}
                          {style}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div style={{ 
                    display: "flex", 
                    gap: "0.75rem", 
                    marginTop: "1rem",
                    paddingTop: "1rem",
                    borderTop: "1px solid #EEE"
                  }}>
                    <button
                      onClick={() => { 
                        setSelectedCity(""); 
                        setClassesFilter(""); 
                        setStyleFilter(""); 
                      }}
                      style={{
                        flex: 1,
                        padding: "1rem",
                        background: "#F5F5F5",
                        color: "#666",
                        border: "none",
                        borderRadius: "12px",
                        fontSize: "1rem",
                        fontWeight: "600",
                        cursor: "pointer",
                        minHeight: "52px"
                      }}
                      data-testid="clear-all-filters-btn"
                    >
                      Clear All
                    </button>
                    <button
                      onClick={() => setShowFilters(false)}
                      style={{
                        flex: 2,
                        padding: "1rem",
                        background: "#1B4D3E",
                        color: "#FFF",
                        border: "none",
                        borderRadius: "12px",
                        fontSize: "1rem",
                        fontWeight: "600",
                        cursor: "pointer",
                        minHeight: "52px"
                      }}
                      data-testid="apply-filters-btn"
                    >
                      Show Events
                    </button>
                  </div>
                </div>
              </>
            )}

            <div style={{ display: "grid", gap: "1.5rem", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}>
              {events
                .filter((event) => {
                  // Past events are filtered server-side in /api/events/stats

                  // Hide unapproved guest host events
                  if (event.event_type === "guest_host" && event.approval_status !== "approved") return false;

                  // City filter
                  if (selectedCity && event.city !== selectedCity) return false;
                  
                  // Classes filter
                  if (classesFilter === "yes" && !event.classes_available) return false;
                  if (classesFilter === "no" && event.classes_available) return false;
                  
                  // Style filter
                  const eventStyles = event.event_type === "guest_host" ? event.styles_of_play : event.allowed_styles;
                  if (styleFilter && !eventStyles?.includes(styleFilter)) return false;
                  
                  return true;
                })
                .sort((a, b) => {
                  // Date asc, then start_time asc — so same-day events render
                  // in chronological order. Backend /events/stats now sorts
                  // server-side; this is defensive (also covers any client-side
                  // re-filter that re-renders the array).
                  const dateDiff = new Date(a.date) - new Date(b.date);
                  if (dateDiff !== 0) return dateDiff;
                  return parseEventTime(a.start_time || a.time) - parseEventTime(b.start_time || b.time);
                })
                .map((event, index) => {
                // Calculate badge status
                const slotsLeft = event.stats?.slots_left ?? 0;
                const isSoldOut = slotsLeft === 0 || event.registration_closed;
                const isAlmostFull = !isSoldOut && slotsLeft <= 4;
                
                // Check if event is new (created within last 2 days)
                const createdAt = new Date(event.created_at);
                const now = new Date();
                const daysSinceCreated = (now - createdAt) / (1000 * 60 * 60 * 24);
                const isNew = daysSinceCreated <= 2;
                
                // Check if event is closing soon (within 2 days of event date)
                const eventDate = new Date(event.date);
                const daysUntilEvent = (eventDate - now) / (1000 * 60 * 60 * 24);
                const isClosingSoon = daysUntilEvent <= 2 && daysUntilEvent >= 0;
                
                return (
                <Link
                  key={event.id}
                  to={`/events/${event.id}`}
                  style={{ 
                    textDecoration: "none", 
                    color: "inherit",
                    cursor: "pointer"
                  }}
                  data-testid={`event-card-${event.id}`}
                >
                  <div className="card" style={{ height: "100%", position: "relative", overflow: "hidden", padding: 0, border: event.is_private ? "2px solid #D32F2F" : "2px solid #1B4D3E", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: "12px" }} data-private={event.is_private ? "true" : undefined}>
                    {/* Venue Image with Overlay */}
                    <div style={{
                      height: "140px",
                      background: "linear-gradient(135deg, #1B4D3E 0%, #2D6A4F 50%, #40916C 100%)",
                      position: "relative",
                      overflow: "hidden"
                    }}>
                      <img
                        src={event.image_url && event.image_url !== "None" && event.image_url !== "" ? event.image_url : "/event-placeholder.jpg"}
                        alt={event.venue_name}
                        loading={index === 0 ? undefined : "lazy"}
                        fetchPriority={index === 0 ? "high" : undefined}
                        decoding="async"
                        width={600}
                        height={140}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover"
                        }}
                        onError={(e) => { e.target.onerror = null; e.target.src = '/event-placeholder.jpg'; }}
                      />

                      {/* Status Badges - on top right of image */}
                      <div style={{
                        position: "absolute",
                        top: "0.5rem",
                        right: "0.5rem",
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.35rem",
                        alignItems: "flex-end"
                      }}>
                        {event.event_type === "guest_host" && (
                          <span style={{
                            background: "#FFF",
                            color: "#1B4D3E",
                            padding: "0.2rem 0.6rem",
                            borderRadius: "20px",
                            fontSize: "0.6rem",
                            fontWeight: "700",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px"
                          }}>
                            Guest Host
                          </span>
                        )}
                        {isNew && !isSoldOut && !isClosingSoon && (
                          <span style={{
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            color: "#FFF",
                            padding: "0.2rem 0.6rem",
                            borderRadius: "20px",
                            fontSize: "0.6rem",
                            fontWeight: "700",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            boxShadow: "0 2px 8px rgba(102, 126, 234, 0.4)"
                          }}
                          data-testid={`badge-new-${event.id}`}
                          >
                            New
                          </span>
                        )}
                        {isClosingSoon && !isSoldOut && (
                          <span style={{
                            background: "linear-gradient(135deg, #E91E63 0%, #C2185B 100%)",
                            color: "#FFF",
                            padding: "0.2rem 0.6rem",
                            borderRadius: "20px",
                            fontSize: "0.6rem",
                            fontWeight: "700",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            boxShadow: "0 2px 8px rgba(233, 30, 99, 0.4)"
                          }}
                          data-testid={`badge-closingsoon-${event.id}`}
                          >
                            Closing Soon
                          </span>
                        )}
                        {isSoldOut ? (
                          <span style={{
                            background: "linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)",
                            color: "#FFF",
                            padding: "0.2rem 0.6rem",
                            borderRadius: "20px",
                            fontSize: "0.6rem",
                            fontWeight: "700",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            boxShadow: "0 2px 8px rgba(211, 47, 47, 0.4)"
                          }}
                          data-testid={`badge-soldout-${event.id}`}
                          >
                            Sold Out
                          </span>
                        ) : isAlmostFull ? (
                          <span style={{
                            background: "linear-gradient(135deg, #FF9800 0%, #F57C00 100%)",
                            color: "#FFF",
                            padding: "0.2rem 0.6rem",
                            borderRadius: "20px",
                            fontSize: "0.6rem",
                            fontWeight: "700",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            boxShadow: "0 2px 8px rgba(255, 152, 0, 0.4)"
                          }}
                          data-testid={`badge-almostfull-${event.id}`}
                          >
                            Almost Full
                          </span>
                        ) : null}
                        {event.classes_available && (
                          <span style={{
                            background: "linear-gradient(135deg, #7B1FA2 0%, #9C27B0 100%)",
                            color: "#FFF",
                            padding: "0.2rem 0.6rem",
                            borderRadius: "20px",
                            fontSize: "0.6rem",
                            fontWeight: "700",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            boxShadow: "0 2px 8px rgba(156, 39, 176, 0.4)"
                          }}
                          data-testid={`badge-classes-${event.id}`}
                          >
                            Classes Available
                          </span>
                        )}
                      </div>
                      
                      {/* Date & Venue Name on image - top left */}
                      <div style={{
                        position: "absolute",
                        top: "0.6rem",
                        left: "0.6rem",
                        right: "3rem"
                      }}>
                        <div style={{ 
                          color: "#FFF",
                          fontWeight: "600",
                          fontSize: "0.8rem",
                          marginBottom: "0.2rem"
                        }}>
                          <span style={{ background: "#000", padding: "0.15rem 0.4rem", borderRadius: "3px" }}>
                            {formatDate(event.date)}
                          </span>
                        </div>
                        <h2 style={{ 
                          fontSize: "1.1rem", 
                          fontWeight: "700", 
                          color: "#FFF",
                          margin: 0,
                          lineHeight: "1.3"
                        }}>
                          <span style={{ background: "#000", padding: "0.15rem 0.4rem", borderRadius: "3px", boxDecorationBreak: "clone", WebkitBoxDecorationBreak: "clone" }}>
                            {event.event_name || event.venue_name}
                          </span>
                        </h2>
                      </div>
                    </div>
                    
                    <div style={{ padding: "0.85rem" }}>
                      {event.event_type === "guest_host" && event.tagline && (
                        <p style={{
                          fontSize: "0.8rem",
                          color: "#444",
                          margin: "0 0 0.5rem 0",
                          lineHeight: "1.4",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden"
                        }}>
                          {event.tagline}
                        </p>
                      )}
                      <div style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.35rem",
                        opacity: 1
                      }}>
                        <span
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.address)}`, '_blank', 'noopener,noreferrer');
                          }}
                          style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            gap: "0.4rem",
                            color: "#1B4D3E",
                            textDecoration: "none",
                            fontSize: "0.8rem",
                            cursor: "pointer"
                          }}
                          data-testid={`map-link-${event.id}`}
                        >
                          <MapPin size={14} style={{ flexShrink: 0 }} />
                          <span style={{ textDecoration: "underline", fontWeight: "500" }}>
                            {event.event_name
                              ? [event.venue_name, event.area].filter(Boolean).join(" · ") + (event.city ? ` (${event.city})` : "")
                              : (event.area ? `${event.area}${event.city ? ` (${event.city})` : ""}` : (event.city || ""))
                            }
                          </span>
                        </span>

                        {(event.slots || []).length > 0 ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: "0.15rem" }}>
                            {event.slots.map((slot, idx) => (
                              <div key={slot.slot_id || idx} style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                <Clock size={14} color="#666660" style={{ flexShrink: 0 }} />
                                <span style={{ color: "#666660", fontSize: "0.8rem" }}>
                                  Slot {idx + 1}: {formatTime(slot.start_time)} – {formatTime(slot.end_time)}{slot.max_players ? ` (${slot.max_players} seats)` : ""}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                            <Clock size={14} color="#666660" style={{ flexShrink: 0 }} />
                            <span style={{ color: "#666660", fontSize: "0.8rem" }}>{formatTime(event.start_time || event.time)}{event.end_time ? ` - ${formatTime(event.end_time)}` : ""}</span>
                          </div>
                        )}

                        {(() => {
                          const isHosted = event.event_type === "guest_host";
                          const hasFee = isHosted
                            ? (event.price && event.price !== "0")
                            : event.cost_per_seat > 0;
                          return hasFee ? (
                            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                              <IndianRupee size={14} color="#1B4D3E" style={{ flexShrink: 0 }} />
                              <span style={{ color: "#1B4D3E", fontWeight: "600", fontSize: "0.8rem" }}>
                                {isHosted
                                  ? `${event.price.startsWith("₹") ? event.price : `₹${event.price}`} per seat`
                                  : <>₹{event.cost_per_seat} per seat{event.fee_redeemable_fnb && (
                                      <span style={{ fontWeight: "400", fontStyle: "italic" }}> (redeemable against F&amp;B)</span>
                                    )}</>}
                              </span>
                            </div>
                          ) : (
                            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                              <IndianRupee size={14} color="#2E7D32" style={{ flexShrink: 0 }} />
                              <span style={{ color: "#2E7D32", fontWeight: "600", fontSize: "0.8rem" }}>Free to Play</span>
                            </div>
                          );
                        })()}

                      {/* Payment - only show if there's a fee */}
                      {event.cost_per_seat > 0 && (
                        <p style={{ fontSize: "0.8rem", color: "#1B4D3E", margin: "0" }}>
                          <strong>Payment:</strong> {event.payment_method === "online" ? "online only" : event.payment_method === "both" ? "online or at venue" : "at venue"}
                        </p>
                      )}
                      {event.event_type === "guest_host" && event.payment_timing && event.payment_timing.length > 0 && (
                        <p style={{ fontSize: "0.8rem", color: "#1B4D3E", margin: "0" }}>
                          <strong>Payment:</strong> {formatPaymentTiming(event.payment_timing)}
                        </p>
                      )}

                      {/* Host - only show if present */}
                      {(event.host || event.host_display_name) && (
                        <p style={{ fontSize: "0.8rem", color: "#1B4D3E", margin: "0" }}>
                          <strong>Host:</strong> {event.host || event.host_display_name}
                        </p>
                      )}

                      <div style={{ marginTop: "0.4rem", paddingTop: "0.4rem", borderTop: "1px solid #E2E2DC" }}>
                        <p style={{ fontSize: "0.75rem", color: "#666660", margin: "0" }}>
                          <strong>Styles:</strong> {(event.event_type === "guest_host" ? event.styles_of_play : event.allowed_styles)?.join(", ")}
                        </p>
                      </div>

                      {/* Signup Stats */}
                      {event.stats && !isSoldOut && (
                        <div style={{ 
                          marginTop: "0.5rem", 
                          padding: "0.5rem", 
                          background: "#F5F7F5", 
                          borderRadius: "8px",
                          display: "grid",
                          gridTemplateColumns: "repeat(3, 1fr)",
                          gap: "0.25rem",
                          textAlign: "center"
                        }}>
                          <div>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.25rem", color: "#1B4D3E" }}>
                              <Table2 size={14} />
                              <span style={{ fontSize: "1rem", fontWeight: "700" }}>{event.stats.total_tables}</span>
                            </div>
                            <p style={{ fontSize: "0.65rem", color: "#666660", marginTop: "0" }}>Tables</p>
                          </div>
                          <div>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.25rem", color: "#1B4D3E" }}>
                              <Users size={14} />
                              <span style={{ fontSize: "1rem", fontWeight: "700" }}>{event.stats.total_players}</span>
                            </div>
                            <p style={{ fontSize: "0.65rem", color: "#666660", marginTop: "0" }}>Players</p>
                          </div>
                          <div>
                            <div style={{ 
                              fontSize: "1rem", 
                              fontWeight: "700", 
                              color: event.stats.slots_left > 0 ? "#2E7D32" : "#D32F2F" 
                            }}>
                              {event.stats.slots_left}
                            </div>
                            <p style={{ fontSize: "0.65rem", color: "#666660", marginTop: "0" }}>Slots Left</p>
                          </div>
                        </div>
                      )}

                      {/* Sign Up Button */}
                      <div style={{ marginTop: "0.75rem" }}>
                        <button
                          style={{
                            width: "100%",
                            padding: "0.6rem 1rem",
                            background: userSignups[event.id]
                              ? "#2E7D32"
                              : (isSoldOut ? "#D32F2F" : "#1B4D3E"),
                            color: "#FFF",
                            border: "none",
                            borderRadius: "8px",
                            fontSize: "0.85rem",
                            fontWeight: "600",
                            cursor: "pointer"
                          }}
                          data-testid={userSignups[event.id] ? `manage-booking-btn-${event.id}` : `details-btn-${event.id}`}
                        >
                          {userSignups[event.id]
                            ? "Manage Your Booking"
                            : (isSoldOut ? "Sold Out" : "Sign Up")
                          }
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                </Link>
              );
              })}
            </div>
          </>
        )}
      </div>
      
      {/* Floating WhatsApp Community Button - Mobile Optimized */}
      <a
        href="https://chat.whatsapp.com/LgawyQzg5jb3YGDQkRiEaO?mode=gi_t"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "56px",
          height: "56px",
          background: "#25D366",
          color: "white",
          borderRadius: "50%",
          boxShadow: "0 4px 12px rgba(37, 211, 102, 0.4)",
          textDecoration: "none",
          zIndex: 1000,
          transition: "transform 0.2s, box-shadow 0.2s"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "scale(1.1)";
          e.currentTarget.style.boxShadow = "0 6px 16px rgba(37, 211, 102, 0.5)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(37, 211, 102, 0.4)";
        }}
        data-testid="whatsapp-community-btn"
        title="Join our WhatsApp Community"
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
      
      {process.env.REACT_APP_ENV === "staging" && (
        <div className="container" style={{ maxWidth: "600px", padding: "0 1rem" }}>
          <div style={{
            border: "2px dashed #8a9e8a",
            borderRadius: "8px",
            padding: "12px 16px",
            margin: "24px 0",
            backgroundColor: "#f0f4f0",
            color: "#4a6a4a",
            fontSize: "0.85rem"
          }}>
            <div style={{ fontWeight: 600, marginBottom: "8px" }}>DEV: Hosted event tools</div>
            {!localStorage.getItem("user_token") ? (
              <p style={{ margin: 0 }}>Log in first to create a test event.</p>
            ) : (
              <>
                <button
                  type="button"
                  onClick={createTestEvent}
                  disabled={devLoading}
                  style={{
                    backgroundColor: "#8a9e8a",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    padding: "6px 12px",
                    cursor: devLoading ? "not-allowed" : "pointer",
                    fontSize: "0.85rem",
                    opacity: devLoading ? 0.7 : 1
                  }}
                >
                  {devLoading ? "Creating..." : "Create test hosted event"}
                </button>
                {devTestEvent && (
                  <div style={{ marginTop: "10px", display: "flex", gap: "12px" }}>
                    <a href={devTestEvent.event_url} target="_blank" rel="noreferrer"
                       style={{ color: "#4a6a4a", fontWeight: 600 }}>
                      View event page
                    </a>
                    <a href={devTestEvent.manage_url} target="_blank" rel="noreferrer"
                       style={{ color: "#4a6a4a", fontWeight: 600 }}>
                      Open host dashboard
                    </a>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default HomePage;